import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { authApi } from '@xtnd/web/features/auth/api';
import { ROUTE_TITLES } from '@xtnd/web/shared/constants/routes';

const DEFAULT_TITLE = 'Optimizing Operations';

/**
 * Converts a route pattern (with :param placeholders) to a regex
 * e.g., '/invoice/:invoiceId/appeal/:appealId' => /^\/invoice\/[^\/]+\/appeal\/[^\/]+$/
 */
function routeToRegex(route: string): RegExp {
  const pattern = route.replace(/:[^/]+/g, '[^/]+').replace(/\//g, '\\/');
  return new RegExp(`^${pattern}$`);
}

/**
 * Sorts routes by specificity:
 * - More segments = higher priority
 * - Exact matches (no params) > parameterized routes
 */
function sortRoutesBySpecificity(routes: string[]): string[] {
  return [...routes].sort((a, b) => {
    const aSegments = a.split('/').length;
    const bSegments = b.split('/').length;
    if (aSegments !== bSegments) return bSegments - aSegments;

    const aHasParams = a.includes(':');
    const bHasParams = b.includes(':');
    if (aHasParams !== bHasParams) return aHasParams ? 1 : -1;

    return b.length - a.length;
  });
}

const SORTED_ROUTES = sortRoutesBySpecificity(Object.keys(ROUTE_TITLES));

export function useDynamicTitle() {
  const { pathname } = useLocation();
  const currentUser = authApi.getCurrentUser();

  useEffect(() => {
    const currentPlatform = import.meta.env.VITE_APP_NAME;
    const moduleTitle = findModuleTitle(pathname);
    document.title = `${currentPlatform} - ${moduleTitle}`;
  }, [pathname, currentUser?.tenantAlias]);
}

function findModuleTitle(pathname: string): string {
  if (ROUTE_TITLES[pathname]) {
    return ROUTE_TITLES[pathname];
  }

  const matchedRoute = SORTED_ROUTES.find(route => {
    if (route === '*') return false;

    if (route.includes(':')) {
      return routeToRegex(route).test(pathname);
    }

    if (route === '/') {
      return false;
    }

    return pathname.startsWith(route);
  });

  if (matchedRoute) {
    return ROUTE_TITLES[matchedRoute] ?? DEFAULT_TITLE;
  }

  return ROUTE_TITLES['*'] ?? DEFAULT_TITLE;
}
